import React from "react";
import { BarChart, Bar, YAxis, CartesianGrid, Tooltip } from "recharts";
import reportAPI from "../../../../api/report";
import { ButtonExcel } from "../../../../utils/buttonLoadExcel";
import { ExcelTotalDay } from "../../functions/Report.ExportTotalDay";
import { formatTotalDay } from "../../utils/Report.Format";
import { displayValue } from "../../../../components/functions/Functions";
import { chartColors } from "../../../../utils/colors";
import {
  BARCHART,
  FLEXDROPDOWN,
  LEFTTABLE,
  RIGHTTABLE,
  SCREENIPAD,
  STYLEBODY,
  STYLEHEAD,
  TABLE,
  THTABLE,
  TRTABLE,
  TXCENTER,
  TXLEFT,
} from "../../../../utils/useStyle";
import DropdownReport from "../../report.dropdwn";
import { GenerateOptionsYear } from "../../../../components/functions/Functions";

const optionsYear = GenerateOptionsYear();

const DropdownFollows = ({ year, setYear }) => {
  return (
    <div>
      <DropdownReport
        title={"ปี"}
        options={optionsYear}
        value={year}
        setValue={setYear}
      />
    </div>
  );
};

// tab04 การติดตามผู้ป่วย
const ReportTimeTotalDay = ({ userDept }) => {
  const [loading, setLoading] = React.useState(false);
  const [year, setYear] = React.useState({
    label: "ทุกปี",
    value: "ALL",
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const count = payload?.[0]?.value || 0;
      const percentage = ((count * 100) / data.number).toFixed(1);
      const count2 = payload?.[1]?.value || 0;
      const percentage2 = ((count2 * 100) / data.number).toFixed(1);
      const count3 = payload?.[2]?.value || 0;
      const percentage3 = ((count3 * 100) / data.number).toFixed(1);
      return (
        <div className="bg-gray-50 text-gray-700 text-sm px-2 py-1 rounded">
          <p className="label">{`All : ${count} (${percentage}%)`}</p>
          <p className="label">{`Adult : ${count2} (${percentage2}%)`}</p>
          <p className="label">{`Child : ${count3} (${percentage3}%)`}</p>
        </div>
      );
    }

    return null;
  };

  const [data, setData] = React.useState({
    A: 0, //1
    B: 0, //2
    C: 0, //3
    D: 0, //4
    E: 0, //5
    F: 0, //6
    G: 0, //7

    //
    A_adult: 0,
    B_adult: 0,
    C_adult: 0,
    D_adult: 0,
    E_adult: 0,
    F_adult: 0,
    G_adult: 0,

    //
    A_kid: 0,
    B_kid: 0,
    C_kid: 0,
    D_kid: 0,
    E_kid: 0,
    F_kid: 0,
    G_kid: 0,

    //

    mean: 0,
    median: 0,
    sd: 0,
    min: 0,
    max: 0,
    per25: 0,
    per75: 0,
    number: 0,
    //
    mean_ad: 0,
    median_ad: 0,
    sd_ad: 0,
    min_ad: 0,
    max_ad: 0,
    per25_ad: 0,
    per75_ad: 0,
    number_ad: 0,
    //
    mean_kid: 0,
    median_kid: 0,
    sd_kid: 0,
    min_kid: 0,
    max_kid: 0,
    per25_kid: 0,
    per75_kid: 0,
    number_kid: 0,
  });

  const listTable = [
    {
      name: "1-7 วัน",
      all: data?.A,
      adult: data?.A_adult,
      kid: data?.A_kid,
    },
    { name: "8-14 วัน", all: data?.B, adult: data?.B_adult, kid: data?.B_kid },
    { name: "15-30 วัน", all: data?.C, adult: data?.C_adult, kid: data?.C_kid },
    { name: "31-90 วัน", all: data?.D, adult: data?.D_adult, kid: data?.D_kid },
    {
      name: "91-180 วัน",
      all: data?.E,
      adult: data?.E_adult,
      kid: data?.E_kid,
    },
    {
      name: "181-365 วัน",
      all: data?.F,
      adult: data?.F_adult,
      kid: data?.F_kid,
    },
    {
      name: "มากกว่า 365 วัน",
      all: data?.G,
      adult: data?.G_adult,
      kid: data?.G_kid,
    },
  ];

  React.useEffect(() => {
    // Use backend API instead of frontend processing
    const fetchData = async () => {
      try {
        const response = await reportAPI.getTotalDayReport({
          year: String(year?.value),
          user_department: userDept,
        });
        if (response?.success) {
          setData(response.data);
        }
      } catch (error) {
        console.error("Error fetching total day report:", error);
      }
    };
    fetchData();
  }, [year, userDept]);

  const table = [
    {
      name: "mean",
      data: data?.mean,
      adult: data?.mean_ad,
      kid: data?.mean_kid,
    },
    {
      name: "median",
      data: data?.median,
      adult: data?.median_ad,
      kid: data?.median_kid,
    },
    { name: "SD", data: data?.sd, adult: data?.sd_ad, kid: data?.sd_kid },
    { name: "min", data: data?.min, adult: data?.min_ad, kid: data?.min_kid },
    { name: "max", data: data?.max, adult: data?.max_ad, kid: data?.max_kid },
    {
      name: "Percentile 25th",
      data: data?.per25,
      adult: data?.per25_ad,
      kid: data?.per25_kid,
    },
    {
      name: "Percentile 75th",
      data: data?.per75,
      adult: data?.per75_ad,
      kid: data?.per75_kid,
    },
    {
      name: "n",
      data: data?.number,
      adult: data?.number_ad,
      kid: data?.number_kid,
    },
  ];

  const excelData = formatTotalDay(data).data[0];

  const totalAll =
    excelData?.A +
    excelData?.B +
    excelData?.C +
    excelData?.D +
    excelData?.E +
    excelData?.F +
    excelData?.G;

  const totalAdult =
    excelData?.A_adult +
    excelData?.B_adult +
    excelData?.C_adult +
    excelData?.D_adult +
    excelData?.E_adult +
    excelData?.F_adult +
    excelData?.G_adult;

  const totalKid =
    excelData?.A_kid +
    excelData?.B_kid +
    excelData?.C_kid +
    excelData?.D_kid +
    excelData?.E_kid +
    excelData?.F_kid +
    excelData?.G_kid;

  const submitExcelData = {
    "1-7": {
      total: excelData?.A,
      total_percent: ((excelData?.A * 100) / totalAll).toFixed(1),
      adults: excelData?.A_adult,
      adults_percent: ((excelData?.A_adult * 100) / totalAdult).toFixed(1),
      children: excelData?.A_kid,
      children_percent: ((excelData?.A_kid * 100) / totalKid).toFixed(1),
    },
    "8-14": {
      total: excelData?.B,
      total_percent: ((excelData?.B * 100) / totalAll).toFixed(1),
      adults: excelData?.B_adult,
      adults_percent: ((excelData?.B_adult * 100) / totalAdult).toFixed(1),
      children: excelData?.B_kid,
      children_percent: ((excelData?.B_kid * 100) / totalKid).toFixed(1),
    },
    "15-30": {
      total: excelData?.C,
      total_percent: ((excelData?.C * 100) / totalAll).toFixed(1),
      adults: excelData?.C_adult,
      adults_percent: ((excelData?.C_adult * 100) / totalAdult).toFixed(1),
      children: excelData?.C_kid,
      children_percent: ((excelData?.C_kid * 100) / totalKid).toFixed(1),
    },
    "31-90": {
      total: excelData?.D,
      total_percent: ((excelData?.D * 100) / totalAll).toFixed(1),
      adults: excelData?.D_adult,
      adults_percent: ((excelData?.D_adult * 100) / totalAdult).toFixed(1),
      children: excelData?.D_kid,
      children_percent: ((excelData?.D_kid * 100) / totalKid).toFixed(1),
    },
    "91-180": {
      total: excelData?.E,
      total_percent: ((excelData?.E * 100) / totalAll).toFixed(1),
      adults: excelData?.E_adult,
      adults_percent: ((excelData?.E_adult * 100) / totalAdult).toFixed(1),
      children: excelData?.E_kid,
      children_percent: ((excelData?.E_kid * 100) / totalKid).toFixed(1),
    },
    "181-365": {
      total: excelData?.F,
      total_percent: ((excelData?.F * 100) / totalAll).toFixed(1),
      adults: excelData?.F_adult,
      adults_percent: ((excelData?.F_adult * 100) / totalAdult).toFixed(1),
      children: excelData?.F_kid,
      children_percent: ((excelData?.F_kid * 100) / totalKid).toFixed(1),
    },
    "365+": {
      total: excelData?.G,
      total_percent: ((excelData?.G * 100) / totalAll).toFixed(1),
      adults: excelData?.G_adult,
      adults_percent: ((excelData?.G_adult * 100) / totalAdult).toFixed(1),
      children: excelData?.G_kid,
      children_percent: ((excelData?.G_kid * 100) / totalKid).toFixed(1),
    },
    Total: {
      total: totalAll,
      total_percent: 100,
      adults: totalAdult,
      adults_percent: 100,
      children: totalKid,
      children_percent: 100,
    },
  };

  return (
    <>
      <div className={``}>
        <div className="my-10">
          <div className={SCREENIPAD}>
            <div className={STYLEHEAD}>
              รายงานจำนวนวันที่ผู้ป่วยแต่ละคนใช้สาย
              <div className={STYLEBODY}>ของปี "{year.label}"</div>
            </div>
            <div className={FLEXDROPDOWN}>
              <DropdownFollows year={year} setYear={setYear} />
              <ButtonExcel
                text={"DOWNLOAD EXCEL"}
                onClick={() => {
                  ExcelTotalDay(submitExcelData);
                }}
                loading={loading}
              />
            </div>
          </div>
          <div className={BARCHART}>
            <BarChart
              width={700}
              height={300}
              data={listTable}
              margin={{
                top: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                label={{ fill: "#111827" }}
                dataKey="all"
                fill={chartColors.chartblue_1}
              />
              <Bar
                label={{ fill: "#111827" }}
                dataKey="adult"
                fill={chartColors.chartblue_2}
              />
              <Bar
                label={{ fill: "#111827" }}
                dataKey="kid"
                fill={chartColors.chartblue_3}
              />
            </BarChart>
          </div>
          <div className="grid grid-cols-7 mx-16 my-6 text-sm">
            <div className=" -rotate-45 transform-gpu text-center">1-7 day</div>
            <div className=" -rotate-45 transform-gpu text-center">
              8-14 day
            </div>
            <div className=" -rotate-45 transform-gpu text-center">
              15-30 day
            </div>
            <div className=" -rotate-45 transform-gpu text-center">
              31-90 day
            </div>
            <div className=" -rotate-45 transform-gpu text-center">
              91-180 day
            </div>
            <div className=" -rotate-45 transform-gpu text-center">
              181-365 day{" "}
            </div>
            <div className=" -rotate-45 transform-gpu text-center">
              {"> 365 day"}
            </div>
          </div>
          <div className="flex justify-center">
            <div className="flex items-center">
              <div className="box-content h-3 w-4 bg-chartblue_1"></div>
              <div className="text-chartblue_1 mx-2">Total</div>
            </div>
            <div className="flex items-center">
              <div className="box-content h-3 w-4 bg-chartblue_2"></div>
              <div className="text-chartblue_2 mx-2">Adult</div>
            </div>
            <div className="flex items-center">
              <div className="box-content h-3 w-4 bg-chartblue_3"></div>
              <div className="text-chartblue_3 mx-2">Children</div>
            </div>
          </div>
        </div>
      </div>

      <div className=" mx-10">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={LEFTTABLE}>รายการ</th>
              <th className={THTABLE}>ทั้งหมด</th>
              <th className={THTABLE}>ผู้ใหญ่</th>
              <th className={RIGHTTABLE}>เด็ก</th>
            </tr>
          </thead>
          <tbody>
            {table?.map((item, index) => (
              <tr key={index} className={TRTABLE}>
                <td className={TXLEFT}> {item?.name}</td>
                <td className={TXCENTER}>{displayValue(item?.data)}</td>
                <td className={TXCENTER}>{displayValue(item?.adult)}</td>
                <td className={TXCENTER}>{displayValue(item?.kid)} </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ReportTimeTotalDay;
